import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  Image,
  FlatList,
  Animated,
  Dimensions,
  ActivityIndicator,
  ScrollView,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { AppImages } from "../../utility/AppImages";
import { PanGestureHandler } from "react-native-gesture-handler";
import * as Linking from "expo-linking";
import { toast } from "react-toastify";
import { fetchPaymentCreateSession } from "../../services/profileAPI";
import { useLiveStream } from "../../context/LiveStreamContext";
import { getUserLocationDetails } from "../../common/getLocation";

const { height: screenHeight, width: screenWidth } = Dimensions.get("window");

// Calculate number of columns based on screen width
const getNumColumns = () => {
  if (screenWidth >= 300) return 3; // Large phones/tablets
  return 2; // Small phones
};

interface TokenPackage {
  tokens: number;
  price: string;
  exchangeRate: string;
}

interface TokenPurchaseBottomSheetProps {
  visible: boolean;
  onClose: () => void;
  tokenPackages: TokenPackage[];
  selectedPackage: TokenPackage | null;
  handlePackageSelect: (pkg: TokenPackage) => void;
}

const TokenPurchaseBottomSheet: React.FC<TokenPurchaseBottomSheetProps> = ({
  visible,
  onClose,
  tokenPackages,
  selectedPackage,
  handlePackageSelect,
}) => {
  const { currentEventId, loginData } = useLiveStream();
  const [loading, setLoading] = useState(false);
  const slideAnim = useRef(new Animated.Value(screenHeight * 0.5)).current;
  const [numColumns, setNumColumns] = useState(3);

  useEffect(() => {
    if (visible) {
      // Slide up when visible - slide to 0 to show the bottom sheet
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        tension: 65,
        friction: 10,
      }).start();
    } else {
      // Slide down when not visible - slide to full height to hide
      Animated.timing(slideAnim, {
        toValue: screenHeight * 0.5,
        duration: 250,
        useNativeDriver: true,
      }).start();
    }
  }, [visible]);

  const handleGestureEnd = (event: any) => {
    // Disabled swipe-to-close - only allow closing by clicking outside
    // This prevents accidental closure when scrolling
  };

  const handlePurchase = async () => {
    if (!selectedPackage) {
      toast.error("Please select a token package to purchase.");
      return;
    }

    setLoading(true);

    try {
      const tokensToPurchase = selectedPackage.tokens;
      const price = selectedPackage.price;
      const location = await getUserLocationDetails();
      const countryCode = location?.country_code || "GB"; // Default to UK if location not available
      const sessionData = await fetchPaymentCreateSession(
        loginData?.accessToken,
        tokensToPurchase,
        price,
        currentEventId,
        countryCode,
        "stream"
      );

      console.log("Payment session response:", sessionData);

      if (sessionData.statusCode === 303 && sessionData.body?.url) {
        Linking.openURL(sessionData.body.url);
        onClose(); // Close the modal after opening payment URL
      } else {
        const errorMsg =
          sessionData.body?.message ||
          sessionData.message ||
          "Error creating payment session. Please try again.";
        console.error("Payment session failed:", {
          statusCode: sessionData.statusCode,
          body: sessionData.body,
          fullResponse: sessionData,
        });
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Payment session error:", error);
      toast.error("Payment failed. Please check your network and try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderTokenPackage = ({
    item,
    index,
  }: {
    item: TokenPackage;
    index: number;
  }) => {
    const isSelected = selectedPackage?.tokens === item.tokens;

    // Calculate base tokens and bonus
    const baseTokens = item.tokens > 88 ? item.tokens - 77 : item.tokens;
    const hasBonus = item.tokens > 88;

    return (
      <View
        style={{
          width: screenWidth >= 100 || true ? "33.3%" : "48%",
        }}
      >
        <TouchableOpacity
          onPress={() => handlePackageSelect(item)}
          style={{ position: "relative" }}
        >
          {/* Badge image at top right */}
          {hasBonus && (
            <Image
              source={AppImages.Ko_Token_Bonus_Badge}
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: 45,
                height: 14,
                zIndex: 10,
                transform: [{ translateX: -8 }, { translateY: 8 }],
              }}
              resizeMode="contain"
            />
          )}

          <LinearGradient
            colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 10,
              borderRadius: 10,
              marginVertical: 2,
              marginHorizontal: 2,
              paddingVertical: 0,
              paddingHorizontal: 8,
              borderWidth: isSelected ? 2 : 0.8,
              borderColor: isSelected
                ? AppColors.appViolentColor_6D08C3
                : "rgba(139, 147, 176, 0.10)",
              minHeight: 95,
            }}
          >
            {/* Image + Token number + bonus text in row */}
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 5,
                marginTop: hasBonus ? 8 : 0,
                width: "100%",
              }}
            >
              <Image
                source={AppImages.Ko_Token}
                style={{
                  height: 32,
                  width: 32,
                }}
              />
              <View style={{ gap: 2, flex: 1 }}>
                <Text
                  style={{
                    fontFamily: "Anybody_700Bold",
                    fontSize: 16,
                    fontWeight: "700",
                    lineHeight: 18,
                    color: AppColors.koBrandRed,
                  }}
                  numberOfLines={1}
                >
                  {item.tokens.toLocaleString()}
                </Text>
                {hasBonus && (
                  <Text
                    style={{
                      fontFamily: "Anybody_400Regular",
                      fontSize: 8,
                      fontWeight: "400",
                      lineHeight: 10,
                      color: AppColors.gray_8B93B0,
                    }}
                    numberOfLines={1}
                  >
                    {baseTokens.toLocaleString()} + 77
                  </Text>
                )}
              </View>
            </View>

            {/* Price at bottom */}
            <Text
              style={{
                color: "#FFF",
                textAlign: "center",
                fontSize: 14,
                fontWeight: "700",
                lineHeight: 14,
              }}
              numberOfLines={1}
            >
              {item.price}
            </Text>
          </LinearGradient>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <Modal transparent visible={visible} animationType="none">
      <View style={{ flex: 1 }}>
        <TouchableOpacity
          style={{ flex: 1 }}
          onPress={onClose}
          activeOpacity={1}
        />

        <PanGestureHandler onHandlerStateChange={handleGestureEnd}>
          <Animated.View
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: screenHeight * 0.5,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              transform: [{ translateY: slideAnim }],
              overflow: "hidden",
            }}
          >
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                flex: 1,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
              }}
            >
              {/* Handle */}
              <View
                style={{
                  width: 40,
                  height: 4,
                  backgroundColor: AppColors.koBrandLightGray,
                  alignSelf: "center",
                  marginTop: 8,
                  borderRadius: 2,
                }}
              />

              {/* Header */}
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingHorizontal: 20,
                  paddingVertical: 12,
                }}
              >
                <Text
                  style={[AppFontStyle.BOLD_ANYBODY_18, { color: "white" }]}
                >
                  Get Tokens
                </Text>
              </View>

              {/* Token Packages Grid */}
              <ScrollView
                style={{ paddingHorizontal: 5, flex: 1 }}
                contentContainerStyle={{ paddingBottom: 90 }}
                showsVerticalScrollIndicator={true}
              >
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    justifyContent: "flex-start",
                    marginBottom: 5,
                  }}
                >
                  {tokenPackages.map((item, index) =>
                    renderTokenPackage({ item, index })
                  )}
                </View>
              </ScrollView>

              {/* Bottom Purchase Button - Always show */}
              <LinearGradient
                colors={["#111827", "#000000"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: 20,
                }}
              >
                <TouchableOpacity
                  onPress={handlePurchase}
                  disabled={!selectedPackage || loading}
                >
                  <LinearGradient
                    colors={
                      loading
                        ? ["#6B7280", "#6B7280"]
                        : !selectedPackage
                        ? ["#6B7280", "#6B7280"]
                        : ["#DC2626", "#991B1B"]
                    }
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={{
                      height: 38,
                      paddingVertical: 10,
                      paddingHorizontal: 24,
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "row",
                      gap: 10,
                      borderRadius: 6,
                    }}
                  >
                    {loading ? (
                      <ActivityIndicator size="small" color="white" />
                    ) : (
                      <>
                        {selectedPackage && (
                          <Image
                            source={AppImages.Ko_Token}
                            style={{ width: 20, height: 20 }}
                            resizeMode="contain"
                          />
                        )}
                        <Text
                          style={[
                            AppFontStyle.BOLD_ANYBODY_16,
                            { color: "white" },
                          ]}
                        >
                          {selectedPackage
                            ? `Get ${selectedPackage.tokens} (${selectedPackage.price})`
                            : "Select a package"}
                        </Text>
                      </>
                    )}
                  </LinearGradient>
                </TouchableOpacity>

                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_10,
                    {
                      color: AppColors.koBrandLightGray,
                      textAlign: "center",
                      marginTop: 8,
                    },
                  ]}
                >
                  By continuing you agree to Token Policy
                </Text>
              </LinearGradient>
            </LinearGradient>
          </Animated.View>
        </PanGestureHandler>
      </View>
    </Modal>
  );
};

export default TokenPurchaseBottomSheet;
